<template>
  <div class="login login-container fill-height mt-1 white">
    <v-container fluid fill-height>
      <v-layout>
        <v-flex xs12>
          <v-form
            ref="form"
            v-model="valid"
            @submit.prevent.stop="handleSubmit"
          >
            <v-card class="elevation-0 px-0 px-sm-7 pb-5">
              <div
                class="text-h1 line-height-1 default--text text-center mt-6 mb-2"
              >
                {{ $t("login.title") }}
              </div>
              <v-spacer></v-spacer>
              <v-card-text>
                <v-text-field
                  v-if="!showMfa"
                  color="primary"
                  v-model="email"
                  :error-messages="errors"
                  type="email"
                  :label="$t('login.email')"
                  autocomplete="off"
                  class="mb-2"
                  required
                  dense
                  outlined
                  clearable
                ></v-text-field>
                <v-text-field
                  v-if="!showMfa"
                  color="primary"
                  v-model="password"
                  :label="$t('login.password')"
                  autocomplete="off"
                  :append-icon="showPassword ? '$eye' : '$eyeOff'"
                  :type="showPassword ? 'text' : 'password'"
                  :rules="passwordRules"
                  @click:append="toggleShowPassword"
                  required
                  dense
                  outlined
                  clearable
                ></v-text-field>

                <template v-if="showMfa">
                  <template v-if="isEmailAddress">{{
                    $t("advancedSecurity.mfaCodeSentToEmail", [email])
                  }}</template>
                  <template v-else>{{
                    $t(
                      "advancedSecurity.mfaCodeSentToTigrosCardAlternativeMessage"
                    )
                  }}</template>
                  <v-otp-input
                    v-model="mfaPin"
                    :error-messages="errors"
                    :label="$t('login.mfaCode')"
                    :rules="mfaCodeRules"
                    dense
                    outlined
                    clearable
                /></template>

                <div class="d-flex flex-column remember-password">
                  <v-checkbox
                    color="primary"
                    class="mt-0 mb-4 align-self-center"
                    hide-details
                    v-model="rememberMe"
                  >
                    <template v-slot:label>
                      <div class="text-caption">
                        {{ $t("login.rememberOnDevice") }}
                      </div>
                    </template>
                  </v-checkbox>

                  <div class="mt-2" style="margin-bottom: 20px;" v-if="showMfa">
                    <v-btn
                      v-if="showResendOtp"
                      large
                      text
                      width="100%"
                      color="primary"
                      @click="resendMfaPinAndLogin"
                    >
                      Invia di nuovo il codice
                    </v-btn>
                    <span v-else>
                      Puoi richiedere un nuovo codice tra
                      {{ resendOtpCountdown }}
                      secondi
                    </span>
                  </div>

                  <v-btn
                    :loading="loading"
                    type="submit"
                    color="primary"
                    large
                    block
                    depressed
                  >
                    {{ $t("login.buttonLogin") }}
                  </v-btn>
                  <RecaptchaDisclaimer />
                  <ResponseMessage :response="response" class="mt-3" />
                  <div
                    class="mt-2 d-flex justify-content justify-center"
                    style="margin-bottom: 20px; font-weight: bold; font-size: 14px;"
                    v-if="resendConfirmEmail"
                  >
                    <v-btn
                      v-if="!confirmEmailSent && !showSingleError"
                      large
                      text
                      dense
                      outlined
                      clearable
                      width="100%"
                      class="primary--text"
                      @click="resendConfirmEmailCode"
                    >
                      Invia di nuovo la mail di conferma
                    </v-btn>
                    <span v-else-if="confirmEmailSent && !showSingleError">
                      Mail di conferma inviata. Controlla la tua casella di
                      posta.
                    </span>
                  </div>
                  <v-btn
                    v-if="isCordova"
                    :loading="loadingCardCode"
                    class="mt-0 mb-1"
                    color="secondary"
                    large
                    block
                    depressed
                    @click="scanCard()"
                  >
                    {{ $t("login.buttonScanCard") }}
                  </v-btn>
                  <div class="default--text mt-2 font-weight-bold text-right">
                    <a
                      @click="goToResetPassword"
                      class="text-decoration-underline text-caption"
                      :class="$vuetify.breakpoint.xs ? 'default--text' : ''"
                    >
                      {{ $t("login.rememberPassword") }}
                    </a>
                  </div>
                </div>

                <span
                  v-if="enableRegistration"
                  class="d-block text-center mt-7 mb-2 primary--text text-h2 line-height-1 mt-2 font-weight-bold"
                  >{{ $t("login.socialLoginText") }}</span
                >
                <div
                  v-if="enableRegistration"
                  class="social-login d-flex justify-space-between"
                >
                  <!-- <div class="w-50 pr-3"> -->
                  <div class="w-100">
                    <v-btn
                      @click.stop.prevent="socialLoginGoogle()"
                      outlined
                      large
                      block
                      depressed
                      color="primary"
                      class="google-btn"
                    >
                      <img
                        class="mr-2"
                        height="20"
                        width="20"
                        src="/img_layout/social/logo-google.svg"
                        alt="logo google"
                      />
                      <span>{{ $t("login.buttonGoogle") }}</span>
                    </v-btn>
                  </div>
                  <!-- <div class="w-50 pl-3">
                    <v-btn
                      @click.stop.prevent="facebookLogin()"
                      color="#3D6AD6"
                      depressed
                      large
                      block
                      class="facebook-btn white--text"
                    >
                      <v-icon class="mr-1">$facebook</v-icon>
                      <span>{{ $t("login.buttonFacebook") }}</span>
                    </v-btn>
                  </div> -->
                </div>
              </v-card-text>
              <v-divider v-if="enableRegistration" />
              <v-card-actions
                v-if="enableRegistration"
                class="d-flex flex-column default--text"
              >
                <div class="text-h2">
                  {{ $t("login.messageRegister") }}
                </div>
                <v-btn
                  @click="$emit('submit', false)"
                  class="primary--text"
                  large
                  outlined
                  block
                  depressed
                  :to="{ name: 'RegistrationUser', path: '/registration-user' }"
                >
                  {{ $t("login.buttonRegister") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<style lang="scss">
.login-container {
  .default--text {
    a {
      color: var(--v-default-base) !important;
      font-weight: bold !important;
    }
  }
}
</style>

<script>
import ResponseMessage from "@/components/ResponseMessage";
// import CheckDisclaimerDialog from "@/components/profile/CheckDisclaimerDialog.vue";
import RecaptchaDisclaimer from "~/components/RecaptchaDisclaimer.vue";

import pushNotification from "~/mixins/pushNotification";

import recaptcha from "~/mixins/recaptcha";

import { mapActions, mapGetters } from "vuex";

import SocialService from "~/service/socialService";
// import SecurityService from "~/service/securityService";

import {
  requiredValue,
  isPasswordMinimumLength
} from "~/validator/validationRules";
import tigrosCustomService from "@/service/tigrosCustomService";

export default {
  name: "Login",
  props: {
    hideLogo: { type: Boolean, required: false, default: false },
    hideRegistration: { type: Boolean, required: false, default: false },
    redirectParams: { type: Object, required: false }
  },
  mixins: [pushNotification, recaptcha],
  components: { ResponseMessage, RecaptchaDisclaimer },
  data() {
    return {
      loading: null,
      loadingCardCode: null,
      showPassword: false,
      email: "",
      password: "",
      mfaPin: "",
      showMfa: false,
      resendOtpTimer: null,
      resendOtpCountdown: 60, // seconds
      resendMfaPin: false,
      resendConfirmEmail: false,
      confirmEmailSent: false,
      showSingleError: false,
      rememberMe: true,
      response: {},
      valid: true,
      lazy: true,
      passwordRules: [
        requiredValue("Digitare la password"),
        isPasswordMinimumLength()
      ],
      errors: [],
      invalid: null,
      enableRegistration: process.env.VUE_APP_ENABLE_REGISTRATION == "true",
      showResendOtp: false
    };
  },
  computed: {
    isEmailAddress() {
      return this.email && this.email.indexOf("@") != -1;
    }
  },
  watch: {
    showMfa(newVal) {
      if (newVal) {
        this.startResendOtpTimer();
      } else {
        this.clearResendOtpTimer();
      }
    }
  },
  methods: {
    ...mapGetters({
      isAuthenticated: "cart/isAuthenticated"
    }),
    ...mapActions({
      doLogin: "cart/doLogin",
      loadCart: "cart/loadCart"
    }),
    goToResetPassword() {
      this.$router.push({
        name: "ResetPasswordRequest"
      });
      this.$emit("submit", false);
    },
    toggleShowPassword(e) {
      e.preventDefault();
      this.showPassword = !this.showPassword;
    },
    resendConfirmEmailCode() {
      let _this = this;
      tigrosCustomService.resendConfirmEmail(this.email, this.password).then(
        function(data) {
          console.log(data);
          _this.confirmEmailSent = true;
        },
        function(error) {
          console.log(error);
        }
      );
    },
    doFBLogin() {
      var _this = this;
      window.facebookConnectPlugin.login(
        ["public_profile", "email"],
        function(userData) {
          SocialService.facebookLoginConnect(
            userData.authResponse.accessToken,
            true
          ).then(
            function(data) {
              if (data.user.login && data.user.login != "") {
                if (data.data.socialRedirectUrl) {
                  data.data.socialRedirectUrl = "/dashboard";
                }
                _this.$emit("submit", false);
                global.vm.$router
                  .push({
                    path: data.data.socialRedirectUrl
                  })
                  .catch(err => err);
              } else {
                //NUOVO UTENTE
                if (data.data.socialRedirectUrl) {
                  data.data.socialRedirectUrl =
                    "/registration-nocard?social_login=facebook";
                }
                _this.$emit("submit", false);
                global.vm.$router
                  .push({
                    path: data.data.socialRedirectUrl
                  })
                  .catch(err => err);
                // SecurityService.refreshLogin(
                //   data.socialRedirectUrl.indexOf("registration") >= 0
                // ).then(
                //   function() {
                //     this.closeLogin();
                //   },
                //   function() {
                //     this.closeLogin();
                //   }
                // );
              }
            },
            function(error) {
              console.log(error);
            }
          );
        },
        function(error) {
          console.log(error);
        }
      );
    },
    facebookLogin() {
      if (this.isCordova) {
        this.doFBLogin();
      } else {
        let redirectBaseUrl = `${window.location.protocol}//${
          window.location.hostname
        }${
          window.location.port !== "80" &&
          window.location.port !== "443" &&
          window.location.port &&
          window.location.port !== ""
            ? ":" + window.location.port
            : ""
        }`;
        SocialService.openFacebookLoginPage(redirectBaseUrl);
      }
    },
    socialLoginGoogle() {
      if (this.isCordova) {
        let redirectBaseUrl =
          window.location.protocol +
          "//" +
          window.location.hostname +
          (window.location.port !== "80" &&
          window.location.port !== "443" &&
          window.location.port &&
          window.location.port !== ""
            ? ":" + window.location.port
            : "");

        let url =
          "https://accounts.google.com/o/oauth2/auth?client_id=" +
          global.config.googleClientId +
          "&redirect_uri=" +
          redirectBaseUrl +
          "/ebsn/api/social-redir/google&prompt=consent&response_type=code&scope=https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile";

        SocialService.handleGoogleOnApp(url, "registration-nocard");
      } else {
        SocialService.openGoogleLoginPage();
      }
      this.$emit("submit", false);
    },
    scanCard() {
      if (window.cordova && window.cordova.plugins.barcodeScanner) {
        try {
          this.loadingCardCode = true;
          // eslint-disable-next-line no-undef
          cordova.plugins.barcodeScanner.scan(
            result => {
              if (result) {
                this.loadingCardCode = false;
                // todo get card code and go to registration
              }
            },
            error => {
              this.loadingCardCode = false;
              console.error("Scanning failed", error);
            },
            {
              showFlipCameraButton: true,
              showTorchButton: true,
              resultDisplayDuration: 500, // Android, display scanned text for X ms. 0 suppresses it entirely, default 1500
              formats:
                this.formats ||
                "EAN_8,EAN_13,CODE_128,CODE_39,CODE_93,CODABAR,UPC_A,UPC_E"
            }
          );
        } catch (err) {
          console.log(err);
        }
      }
    },
    startResendOtpTimer() {
      this.showResendOtp = false;
      this.resendOtpCountdown = 60;
      if (this.resendOtpTimer) clearInterval(this.resendOtpTimer);
      this.resendOtpTimer = setInterval(() => {
        this.resendOtpCountdown--;
        if (this.resendOtpCountdown <= 0) {
          this.showResendOtp = true;
          clearInterval(this.resendOtpTimer);
          this.resendOtpTimer = null;
        }
      }, 1000);
    },
    clearResendOtpTimer() {
      if (this.resendOtpTimer) {
        clearInterval(this.resendOtpTimer);
        this.resendOtpTimer = null;
      }
      this.showResendOtp = false;
    },
    resendMfaPinAndLogin(e) {
      e.preventDefault();
      this.resendMfaPin = true;
      this.handleSubmit(e);
      this.resendMfaPin = false;
      this.showResendOtp = false;
      this.startResendOtpTimer();
    },
    async handleSubmit() {
      let _this = this;
      let token = null;
      if (
        _this.showMfa &&
        !_this.resendMfaPin &&
        (!_this.mfaPin || _this.mfaPin.length === 0 || _this.mfaPin.length < 6)
      ) {
        return;
      }
      if (_this.password.length > 0) {
        try {
          _this.confirmEmailSent = false;
          _this.resendConfirmEmail = false;
          _this.showSingleError = false;
          _this.loading = true;
          _this.response = {};
          await _this.doLogin({
            email: _this.email,
            password: _this.password,
            rememberMe: _this.rememberMe,
            token: token,
            mfaPin: _this.mfaPin,
            resendMfaPin: _this.resendMfaPin
          });
          if (_this.isAuthenticated) {
            global.EventBus.$emit("login");
            global.EventBus.$emit("loggedIn", true);
          }
          this.$emit("submit", true);
          if (_this.redirectParams) {
            _this.$router.push({
              name: _this.redirectParams.name,
              path: _this.redirectParams.path,
              params: _this.redirectParams.params
            });
          }
          this.$recaptchaInstance?.hideBadge();
        } catch (response) {
          this.response = response;
          if (this.response.status == 3) {
            this.resendConfirmEmail = true;
            if (this.response.errorsMessage.length > 1) {
              this.showSingleError = true;
              this.response.errorsMessage = this.response.errorsMessage.filter(
                error => {
                  return error.code != "error.login.not_confirmed";
                }
              );
            }
          }
          if (
            this.response.errorsMessage &&
            this.response.errorsMessage.length > 0 &&
            this.response.errors
          ) {
            const mainMsg = this.response.errorsMessage[0].message;
            this.response.errors = this.response.errors.filter(
              err => err.error === mainMsg
            );
          }
          if (response.status == 4) {
            //need mfa
            _this.showMfa = true;
          }
        } finally {
          this.loading = false;
        }
      }
    }
  },
  beforeDestroy() {
    this.clearResendOtpTimer();
    this.confirmEmailSent = false;
    this.resendConfirmEmail = false;
  }
};
</script>
